import { Form, Input, Button, message, Spin, Card, Alert } from 'antd';
import { useParams, useNavigate } from 'react-router';
import { useEffect } from 'react';
import { logger } from '@ui/utils/logger';
import {
    useGetSourceByIdQuery,
    useUpdateSourceMutation,
    useGetImagingSourceByIdQuery,
    useUpdateImagingSourceMutation,
} from '@client/features/SourceRegistry/services/api';
import type { Source, SourceType, SourceConfig } from '@client/features/SourceRegistry/types';
import {
    HttpConfigForm,
    SftpConfigForm,
    S3ConfigForm,
    SnowflakeConfigForm,
    EpicConfigForm,
    EhrConfigForm,
    HealthLakeConfigForm,
    AwsHealthImagingConfigForm,
    DicomWebConfigForm,
} from '../../ConfigurationForms';
import { ConfigFormProvider } from '../../ConfigurationForms/ConfigFormContext';
import { filterEmptyCredentials, stripCredentials } from '../../ConfigurationForms/credentialUtils';
import { Routes } from '@packages/enums';

interface FormData {
    sourceName: string;
    sourceType: string;
    schedule: string | null;
    configuration?: SourceConfig;
}

function EditSource() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm<FormData>();

    // Check if ID is a number (regular source) or UUID (medical imaging source)
    const isNumericId = Boolean(id && !isNaN(Number(id)));

    // Try fetching as regular source (only if ID is numeric)
    const {
        data: regularSource,
        isLoading: isLoadingRegular,
        error: errorRegular,
    } = useGetSourceByIdQuery(Number(id), {
        skip: !id || !isNumericId,
    });

    // Try fetching as medical imaging source (only if ID is not numeric or if regular source failed)
    const {
        data: imagingSourceRaw,
        isLoading: isLoadingImaging,
        error: errorImaging,
    } = useGetImagingSourceByIdQuery(id || '', {
        skip: !id || isNumericId,
    });

    const [updateSource, { isLoading: isUpdatingRegular }] = useUpdateSourceMutation();
    const [updateImagingSource, { isLoading: isUpdatingImaging }] = useUpdateImagingSourceMutation();

    // Transform Medical Imaging source to match Source interface
    const imagingSource: Source | undefined = imagingSourceRaw
        ? {
              id: imagingSourceRaw.id,
              sourceName: imagingSourceRaw.name,
              sourceType:
                  imagingSourceRaw.sourceType === 'aws-healthimaging'
                      ? ('AWS HealthImaging' as const)
                      : ('DICOM-web' as const),
              createdBy: 'System',
              createdAt: imagingSourceRaw.createdAt,
              updatedAt: imagingSourceRaw.updatedAt,
              lastVisit: imagingSourceRaw.updatedAt,
              status: imagingSourceRaw.isActive ? ('Active' as const) : ('Inactive' as const),
              schedule: null,
              configuration: undefined,
              isMedicalImaging: true,
          }
        : undefined;

    const source = regularSource || imagingSource;
    const isLoading = isLoadingRegular || isLoadingImaging;
    const error = errorRegular || errorImaging;
    const isUpdating = isUpdatingRegular || isUpdatingImaging;

    useEffect(() => {
        if (source) {
            // Strip credentials from configuration before populating the form
            // This ensures sensitive data is never displayed in the UI
            const configWithoutCredentials = source.configuration ? stripCredentials(source.configuration) : undefined;

            form.setFieldsValue({
                sourceName: source.sourceName,
                sourceType: source.sourceType,
                schedule: source.schedule,
                configuration: configWithoutCredentials,
            });
        }
    }, [source, form]);

    const handleFinish = async (values: FormData) => {
        try {
            if (!id) return;

            // Check if this is a medical imaging source
            if (source?.isMedicalImaging) {
                // Prepare update data for medical imaging source
                const updateData: any = {
                    name: values.sourceName,
                    isActive: source.status === 'Active',
                };

                // Add credentials if provided (filter empty credentials first)
                // Backend now supports partial credential updates - send only what's provided
                if (values.configuration) {
                    const filteredConfig = filterEmptyCredentials(values.configuration as any);

                    if (source.sourceType === 'AWS HealthImaging') {
                        // Handle datastore ID separately (it's not a credential)
                        if (filteredConfig.datastoreId !== undefined) {
                            updateData.defaultDatastoreId = filteredConfig.datastoreId;
                        }

                        // Send partial AWS credentials - backend will merge with existing
                        const awsCreds: Record<string, string> = {};
                        if (filteredConfig.accessKeyId) awsCreds.accessKeyId = filteredConfig.accessKeyId;
                        if (filteredConfig.secretAccessKey) awsCreds.secretAccessKey = filteredConfig.secretAccessKey;
                        if (filteredConfig.region) awsCreds.region = filteredConfig.region;
                        if (filteredConfig.sessionToken) awsCreds.sessionToken = filteredConfig.sessionToken;

                        if (Object.keys(awsCreds).length > 0) {
                            updateData.awsCredentials = awsCreds;
                        }
                    } else if (source.sourceType === 'DICOM-web') {
                        // Send partial DICOM-web credentials - backend will merge with existing
                        const dicomCreds: Record<string, string> = {};
                        if (filteredConfig.baseUrl) dicomCreds.baseUrl = filteredConfig.baseUrl;
                        if (filteredConfig.authType) dicomCreds.authType = filteredConfig.authType;
                        if (filteredConfig.username) dicomCreds.username = filteredConfig.username;
                        if (filteredConfig.password) dicomCreds.password = filteredConfig.password;
                        if (filteredConfig.token) dicomCreds.token = filteredConfig.token;

                        if (Object.keys(dicomCreds).length > 0) {
                            updateData.dicomWebCredentials = dicomCreds;
                        }
                    }
                }

                await updateImagingSource({
                    id,
                    data: updateData,
                }).unwrap();
            } else {
                // Update regular source - only send fields that user provided
                const filteredConfiguration = values.configuration
                    ? (filterEmptyCredentials(values.configuration) as SourceConfig)
                    : undefined;

                const updatedSource: Partial<Omit<Source, 'id'>> = {
                    sourceName: values.sourceName,
                    sourceType: values.sourceType as SourceType,
                    schedule: values.schedule,
                    configuration: filteredConfiguration,
                };

                await updateSource({
                    id: Number(id),
                    data: updatedSource,
                }).unwrap();
            }

            message.success('Source updated successfully. New credentials will be used for future operations.');
            navigate(Routes.SOURCE_REGISTRY);
        } catch (error: any) {
            logger.error('Error updating source', error);

            // Provide more specific error messages
            if (error?.data?.message) {
                const errorMessages = Array.isArray(error.data.message)
                    ? error.data.message.join(', ')
                    : error.data.message;
                message.error({ content: `Failed to update source: ${errorMessages}`, key: 'edit-source-error' });
            } else {
                message.error({
                    content: 'Failed to update source. Please check all required fields.',
                    key: 'edit-source-error',
                });
            }
        }
    };

    const handleCancel = () => {
        navigate(Routes.SOURCE_REGISTRY);
    };

    const renderConfigurationForm = () => {
        const sourceType = source?.sourceType;

        switch (sourceType) {
            case 'HTTP Plugin':
                return <HttpConfigForm />;
            case 'SFTP Connector':
                return <SftpConfigForm />;
            case 'S3':
                return <S3ConfigForm />;
            case 'Snowflake':
                return <SnowflakeConfigForm />;
            case 'Epic':
                return <EpicConfigForm />;
            case 'EHR':
                return <EhrConfigForm />;
            case 'AWS HealthLake':
                return <HealthLakeConfigForm />;
            case 'AWS HealthImaging':
                return <AwsHealthImagingConfigForm />;
            case 'DICOM-web':
                return <DicomWebConfigForm />;
            default:
                return (
                    <Alert
                        message="Unknown source type"
                        description="This source type is not supported for editing."
                        type="warning"
                        showIcon
                    />
                );
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Spin size="large" />
            </div>
        );
    }

    if (error || !source) {
        return (
            <div className="p-8">
                <Alert
                    message="Error"
                    description="Failed to load source. The source may not exist or you don't have permission to view it."
                    type="error"
                    showIcon
                />
                <Button
                    onClick={handleCancel}
                    className="mt-4"
                >
                    Back to Source Registry
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-4xl p-8">
            <Card>
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold">Edit Source</h1>
                    <p className="mt-2 text-gray-600">Update the configuration for your data source</p>
                </div>

                <ConfigFormProvider isEditMode={true}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFinish}
                    >
                        <Form.Item
                            name="sourceName"
                            label="Source Name"
                            rules={[{ required: true, message: 'Please enter source name!' }]}
                        >
                            <Input placeholder="Enter source name" />
                        </Form.Item>

                        <Form.Item
                            name="sourceType"
                            label="Source Type"
                        >
                            <Input
                                disabled
                                className="bg-gray-50"
                            />
                        </Form.Item>

                        <div className="mt-6">
                            <h3 className="mb-4 text-lg font-semibold">Configuration</h3>
                            {renderConfigurationForm()}
                        </div>

                        <div className="mt-8 flex justify-end gap-2">
                            <Button
                                onClick={handleCancel}
                                disabled={isUpdating}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isUpdating}
                            >
                                Update Source
                            </Button>
                        </div>
                    </Form>
                </ConfigFormProvider>
            </Card>
        </div>
    );
}

export default EditSource;
